import { diagnostic } from '../../utils/diagnostics';
import { Capacitor } from '@capacitor/core';
import React, { useEffect, useState } from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';
import { isNativeAppleSignInAvailable } from '../../services/appleSignIn';
import ParentAuthLayout from '../../components/Layout/ParentAuthLayout';
import AppDownloadCard from '../../components/Downloads/AppDownloadCard';
import parentArtwork from '../../assets/icons/portal/parent-login.png';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';

const ParentLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const requestedPath =
    location.state?.from?.pathname;

  const requestedSearch =
    location.state?.from?.search || '';

  const requestedHash =
    location.state?.from?.hash || '';

  const parentDestination =
    typeof requestedPath === 'string' &&
    requestedPath.startsWith('/parent') &&
    requestedPath !== '/parent/login' &&
    requestedPath !== '/parent/setup' &&
    requestedPath !== '/parent/invite'
      ? requestedPath
          + requestedSearch
          + requestedHash
      : '/parent';

  const {
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    signInWithAppleWeb,
    user,
    parent,
    initializing,
    parentChecked
  } = useAuth();

  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [error, setError] = useState('');

  const [signupEmailSent, setSignupEmailSent] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  /*
   * ---------------------------------------------------------
   * AUTHENTICATED USER REDIRECT
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (initializing) return;
    if (!user) return;
    if (!parentChecked) return;

    if (parent) {
      navigate(parentDestination, {
        replace: true
      });
    } else {
      navigate('/parent/setup', {
        replace: true
      });
    }
  }, [
    user,
    parent,
    initializing,
    parentChecked,
    parentDestination,
    navigate
  ]);

  /*
   * ---------------------------------------------------------
   * RESET GOOGLE LOADING AFTER BROWSER HISTORY RESTORE
   * ---------------------------------------------------------
   *
   * A cancelled OAuth redirect can restore this page from the
   * browser's back-forward cache with React state still set to
   * loading. The pageshow event is the browser-supported signal
   * that the preserved page has become active again.
   */
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setGoogleLoading(false);
      }
    };

    window.addEventListener(
      'pageshow',
      handlePageShow
    );

    return () => {
      window.removeEventListener(
        'pageshow',
        handlePageShow
      );
    };
  }, []);

  /*
   * ---------------------------------------------------------
   * CHECK WHETHER PARENT EMAIL IS ALREADY REGISTERED
   * ---------------------------------------------------------
   *
   * Supabase intentionally may not return "User already registered"
   * when email confirmation is enabled.
   *
   * Our RPC safely checks the MyPiggy parent table and returns
   * only true/false.
   */
  const checkExistingParentEmail = async (email) => {
    const {
      data,
      error: checkError
    } = await supabase.rpc(
      'parent_email_registered',
      {
        p_email: email
      }
    );

    if (checkError) {
      diagnostic('error', 'parent.login.registration_lookup_failed');

      throw new Error(
        'Unable to check this email right now. Please try again.'
      );
    }

    return data === true;
  };

  /*
   * ---------------------------------------------------------
   * FORM SUBMIT
   * ---------------------------------------------------------
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      loading ||
      googleLoading
    ) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const normalizedEmail =
        formData.email.trim().toLowerCase();

      if (isSignUp) {
        /*
         * -----------------------------------------------------
         * CHECK FOR EXISTING MY PIGGY PARENT
         * -----------------------------------------------------
         */
        const alreadyRegistered =
          await checkExistingParentEmail(
            normalizedEmail
          );

        if (alreadyRegistered) {
          setError(
            'An account already exists with this email. Please sign in instead.'
          );

          return;
        }

        /*
         * -----------------------------------------------------
         * CREATE NEW SUPABASE AUTH ACCOUNT
         * -----------------------------------------------------
         */
        const result = await signUp(
          normalizedEmail,
          formData.password,
          formData.name.trim()
        );

        if (result?.needsEmailConfirmation) {
          setConfirmationEmail(
            normalizedEmail
          );

          setSignupEmailSent(true);

          setResendMessage('');
          setResendError('');

          setFormData((current) => ({
            ...current,
            password: ''
          }));

          return;
        }
      } else {
        await signIn(
          normalizedEmail,
          formData.password
        );
      }
    } catch (submitError) {
      diagnostic('error', 'parent.login.submit_failed');

      setError(
        submitError?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };
    /*
   * ---------------------------------------------------------
   * GOOGLE AUTHENTICATION
   * ---------------------------------------------------------
   */
  const handleGoogleSignIn =
    async () => {
      if (
        loading ||
        googleLoading
      ) {
        return;
      }

      setGoogleLoading(true);
      setError('');

      try {
        await signInWithGoogle({
          flow: 'login',
        });
      } catch (googleError) {
        diagnostic('error', 'parent.login.google_start_failed');

        setError(
          googleError?.message ||
            'Google sign-in could not be started. Please try again.'
        );

        setGoogleLoading(false);
      }
    };

  const handleAppleSignIn = async () => {
    if (loading || googleLoading || appleLoading) return;
    setAppleLoading(true);
    setError('');
    try {
      if (isNativeAppleSignInAvailable()) {
        await signInWithApple();
      } else {
        await signInWithAppleWeb();
      }
    } catch (appleError) {
      if (appleError?.code !== 'APPLE_SIGN_IN_CANCELLED') {
        setError(appleError?.message || 'Apple sign-in failed. Please try again.');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const showAppleSignIn = isNativeAppleSignInAvailable() ||
    (!Capacitor.isNativePlatform() &&
      import.meta.env.VITE_SUPABASE_URL ===
        'https://agyqzewkrfzmuvmhfiuy.supabase.co');

  const isSubmitting =
    loading ||
    googleLoading ||
    appleLoading;

  /*
   * ---------------------------------------------------------
   * RESEND CONFIRMATION EMAIL
   * ---------------------------------------------------------
   */
  const handleResendConfirmation = async () => {
    if (
      resendLoading ||
      !confirmationEmail
    ) {
      return;
    }

    setResendLoading(true);
    setResendMessage('');
    setResendError('');

    try {
      const {
        error: resendAuthError
      } = await supabase.auth.resend({
        type: 'signup',
        email: confirmationEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/parent/login`
        }
      });

      if (resendAuthError) {
        throw resendAuthError;
      }

      setResendMessage(
        'A new confirmation email has been sent. Please check your inbox.'
      );
    } catch (resendSubmitError) {
      diagnostic('error', 'parent.login.confirmation_resend_failed');

      const message =
        String(
          resendSubmitError?.message ||
          ''
        ).toLowerCase();

      if (
        message.includes('rate limit') ||
        message.includes('too many')
      ) {
        setResendError(
          'Please wait a little before requesting another confirmation email.'
        );
      } else {
        setResendError(
          resendSubmitError?.message ||
            'Unable to resend the confirmation email. Please try again.'
        );
      }
    } finally {
      setResendLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * FORM FIELD CHANGES
   * ---------------------------------------------------------
   */
  const handleChange = (e) => {
    const {
      name,
      value
    } = e.target;

    setFormData((current) => ({
      ...current,
      [name]: value
    }));

    if (error) {
      setError('');
    }
  };

  /*
   * ---------------------------------------------------------
   * SWITCH BETWEEN LOGIN / SIGNUP
   * ---------------------------------------------------------
   */
  const handleModeChange = () => {
    setIsSignUp((current) => !current);

    setError('');

    setFormData((current) => ({
      ...current,
      password: '',
      name: ''
    }));
  };

  /*
   * ---------------------------------------------------------
   * RETURN TO SIGN IN AFTER SIGNUP
   * ---------------------------------------------------------
   */
  const handleBackToSignIn = () => {
    setSignupEmailSent(false);
    setIsSignUp(false);
    setError('');

    setResendMessage('');
    setResendError('');

    setFormData({
      email: confirmationEmail,
      password: '',
      name: ''
    });
  };

  /*
   * ---------------------------------------------------------
   * EMAIL CONFIRMATION NOTICE
   * ---------------------------------------------------------
   */
  if (signupEmailSent) {
    return (
      <ParentAuthLayout>
        <div className="max-w-md mx-auto">
          <Card className="p-6 sm:p-8">
            <div className="text-center">
              <MyPiggyIcon name="my-piggy-bank" size="xl" className="mb-5" />

              <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-3">
                Check your email!
              </h2>

              <p className="text-gray-600 dark:text-slate-400 text-lg mb-6">
                You're almost ready to start using MyPiggy.
              </p>

              <div className="bg-blue-50 dark:bg-blue-950/35 border border-blue-100 rounded-xl p-5 mb-6">
                <p className="text-gray-700 dark:text-slate-300 mb-2">
                  We sent a confirmation link to:
                </p>

                <p className="font-semibold text-gray-900 dark:text-slate-100 break-words">
                  {confirmationEmail}
                </p>
              </div>

              <p className="text-gray-600 dark:text-slate-400 leading-relaxed mb-4">
                Open the email from{' '}
                <span className="font-medium text-gray-800 dark:text-slate-200">
                  MyPiggy
                </span>{' '}
                and click{' '}
                <span className="font-medium text-gray-800 dark:text-slate-200">
                  Confirm My Email
                </span>{' '}
                to activate your account.
              </p>

              <p className="text-sm text-gray-500 dark:text-slate-400 leading-relaxed mb-6">
                Don't see the email? Give it a minute and check
                your spam or junk folder.
              </p>

              {resendMessage && (
                <div
                  className="mb-5 bg-green-50 dark:bg-green-950/35 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 text-sm rounded-lg p-3"
                  role="status"
                >
                  {resendMessage}
                </div>
              )}

              {resendError && (
                <div
                  className="mb-5 bg-red-50 dark:bg-red-950/35 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg p-3"
                  role="alert"
                >
                  {resendError}
                </div>
              )}

              <div className="space-y-3">
                <Button
                  type="button"
                  className="w-full"
                  onClick={handleBackToSignIn}
                >
                  Back to Sign In
                </Button>

                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  disabled={resendLoading}
                  className="w-full text-blue-600 dark:text-blue-400 hover:text-blue-500 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendLoading
                    ? 'Sending...'
                    : 'Resend Confirmation Email'}
                </button>
              </div>
            </div>
          </Card>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Real piggy bank. Digital tracking.
              <br />
              Lifelong money lessons.
            </p>
          </div>
        </div>
      </ParentAuthLayout>
    );
  }

  /*
   * ---------------------------------------------------------
   * LOGIN / SIGNUP FORM
   * ---------------------------------------------------------
   */
  return (
    <ParentAuthLayout>
      <section aria-labelledby="parent-login-heading" className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
        <div className="mb-6 text-center">
          <img src={parentArtwork} alt="" className="mx-auto mb-3 h-16 w-16 object-contain lg:hidden" />
          <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.14em] text-teal-700 dark:text-teal-300">MyPiggy Parents</p>
          <h1 id="parent-login-heading" className="text-3xl font-extrabold tracking-tight text-teal-950 dark:text-teal-100">
            {isSignUp ? 'Create your family account' : 'Welcome back'}
          </h1>
          <p className="mt-2 text-base leading-relaxed text-slate-600 dark:text-slate-300">
            {isSignUp ? 'Start your family’s MyPiggy journey.' : 'Sign in to your family dashboard.'}
          </p>
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
            className="flex min-h-12 w-full items-center justify-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 font-bold text-slate-800 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800 dark:focus-visible:ring-offset-slate-900"
          >
            <FcGoogle className="h-6 w-6 shrink-0" aria-hidden="true" />
            <span>{googleLoading ? 'Connecting to Google...' : 'Continue with Google'}</span>
          </button>
          {showAppleSignIn && (
            <button
              type="button"
              onClick={handleAppleSignIn}
              disabled={isSubmitting}
              className="flex min-h-12 w-full items-center justify-center gap-3 rounded-xl border border-black bg-black px-4 py-3 font-bold text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:focus-visible:ring-offset-slate-900"
            >
              <FaApple className="h-6 w-6 shrink-0" aria-hidden="true" />
              <span>{appleLoading ? 'Connecting to Apple...' : 'Continue with Apple'}</span>
            </button>
          )}
        </div>

        <div className="my-6 flex items-center gap-3 text-xs font-semibold text-slate-500 dark:text-slate-400">
          <span aria-hidden="true" className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
          <span>or continue with email</span>
          <span aria-hidden="true" className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
        </div>

        <form onSubmit={handleSubmit} className="parent-auth-form space-y-4" aria-busy={isSubmitting}>
          {isSignUp && (
            <Input label="Parent Name" name="name" value={formData.name} onChange={handleChange} autoComplete="name" disabled={isSubmitting} placeholder="Your name" required />
          )}
          <Input label="Email" type="email" name="email" autoComplete="email" value={formData.email} onChange={handleChange} disabled={isSubmitting} placeholder="Enter your email" required />
          <Input label="Password" type="password" name="password" value={formData.password} onChange={handleChange} autoComplete={isSignUp ? 'new-password' : 'current-password'} disabled={isSubmitting} placeholder={isSignUp ? 'Create a password' : 'Enter your password'} required />
          {!isSignUp && (
            <div className="text-right">
              <button type="button" onClick={() => navigate('/forgot-password')} disabled={isSubmitting} className="min-h-10 rounded-md px-1 text-sm font-bold text-teal-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 disabled:opacity-50 dark:text-teal-300">Forgot your password?</button>
            </div>
          )}
          {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/35 dark:text-red-300">{error}</div>}
          <button type="submit" disabled={isSubmitting} className="min-h-12 w-full rounded-xl bg-teal-700 px-5 py-3 font-extrabold text-white transition hover:bg-teal-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-teal-700 dark:hover:bg-teal-800 dark:focus-visible:ring-offset-slate-900">
            {loading ? (isSignUp ? 'Checking Account...' : 'Signing In...') : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        <div className="mt-5 text-center">
          <button type="button" onClick={handleModeChange} disabled={isSubmitting} className="min-h-11 rounded-md px-1 text-sm font-bold text-teal-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 disabled:opacity-50 dark:text-teal-300">
            {isSignUp ? 'Already have an account? Sign in' : 'New to MyPiggy? Create an account'}
          </button>
        </div>
      </section>

      {!Capacitor.isNativePlatform() && (
        <>
          <div className="mt-5"><AppDownloadCard audience="parents" /></div>
          <div className="mt-4 text-center">
            <button type="button" onClick={() => navigate('/kid/login')} disabled={isSubmitting} className="min-h-11 rounded-md px-2 text-sm font-bold text-slate-600 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 disabled:opacity-50 dark:text-slate-300">Looking for Kid Login? →</button>
          </div>
        </>
      )}
    </ParentAuthLayout>
  );
};

export default ParentLogin;
